import React, { useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Megaphone, Plus, Trash2, Loader2 } from 'lucide-react';
import { CollapsibleSection } from './CollapsibleSection';

const AC_STAGES = ['response_received', 'first_call_meeting', 'nda', 'on_site', 'loi_signed', 'due_diligence', 'signing', 'closing'];

export const AcKeyUpdatesCard = ({ project }) => {
  const [updates, setUpdates] = useState(project?.ac_key_updates || []);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);

  if (!project || (!AC_STAGES.includes(project.pipeline_stage) && updates.length === 0)) return null;

  const handleAdd = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      const res = await api.addAcKeyUpdate(project.id, text.trim());
      setUpdates(prev => [...prev, res.data]);
      setText('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add'); }
    setSaving(false);
  };

  const handleDelete = async (entry) => {
    try {
      await api.deleteAcKeyUpdate(project.id, entry.id);
      setUpdates(prev => prev.filter(u => u.id !== entry.id));
    } catch { toast.error('Failed to remove'); }
  };

  return (
    <CollapsibleSection
      storageKey={`deal.${project.id}.ac-updates`}
      icon={Megaphone}
      iconClass="text-amber-400"
      title="AC Key Updates"
      count={updates.length || undefined}
      testId="ac-key-updates-card"
    >
      <div className="px-5 py-3 space-y-2">
        {updates.length > 0 && (
          <ul className="space-y-1.5">
            {[...updates].reverse().map(u => (
              <li key={u.id} className="flex items-start gap-2 text-xs text-foreground" data-testid={`ac-key-update-${u.id}`}>
                <span className="text-amber-400 mt-0.5">•</span>
                <span className="flex-1">{u.text}
                  <span className="text-[10px] text-muted-foreground/60 font-mono ml-2">
                    {u.by || ''}{u.at ? ` · ${new Date(u.at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}` : ''}
                  </span>
                </span>
                <button onClick={() => handleDelete(u)} data-testid={`ac-key-update-delete-${u.id}`}
                  className="p-0.5 text-muted-foreground/40 hover:text-red-400 transition-colors">
                  <Trash2 className="w-3 h-3" />
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="flex items-center gap-2">
          <input value={text} onChange={e => setText(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
            placeholder="Add a key update for the next AC meeting…" data-testid="ac-key-update-input"
            className="flex-1 bg-secondary/40 border border-border rounded-sm px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50" />
          <button onClick={handleAdd} disabled={saving || !text.trim()} data-testid="ac-key-update-add-btn"
            className="inline-flex items-center gap-1 text-[11px] text-amber-400 hover:text-amber-300 border border-amber-900/40 rounded-sm px-2.5 py-2 transition-colors disabled:opacity-40">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} Add
          </button>
        </div>
      </div>
    </CollapsibleSection>
  );
};
